import { useEffect, useRef, useState } from "react";
import { api, API_BASE, MARKETPLACES, brl } from "../lib/api";
import { HubStatusBadge, MarketStatusBadge, MarketplaceTag } from "../components/StatusBadge";
import { toast } from "sonner";
import { Loader2, FileText, Printer, RefreshCw, Search } from "lucide-react";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ company_id: "", marketplace: "", hub_status: "" });
  const [search, setSearch] = useState("");
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const load = async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) setLoading(true);
    try {
      const q = new URLSearchParams();
      Object.entries(filtersRef.current).forEach(([k, v]) => v && q.append(k, v));
      const { data } = await api.get(`/orders?${q.toString()}`);
      setOrders(data);
    } catch (e) {
      if (!silent) toast.error("Erro ao listar pedidos");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters]);

  // Auto-refresh every 45s (silent — no loader flicker). Pauses when the
  // tab is hidden so we don't burn API quota on a backgrounded window.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") {
        load({ silent: true });
      }
    }, 45000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);

  const visible = orders.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(s) ||
      o.customer_name?.toLowerCase().includes(s) ||
      o.customer_doc?.includes(s) ||
      o.items?.some((it) => it.sku?.toLowerCase().includes(s))
    );
  });

  return (
    <div className="space-y-4" data-testid="orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Operação</div>
          <h1 className="text-3xl font-black tracking-tighter">Pedidos</h1>
          <p className="text-sm text-slate-600 mt-1">
            Importação centralizada de todos os marketplaces com status fiscal e operacional.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="orders-refresh"
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white flex items-center gap-2"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      <div className="border border-slate-200 bg-white p-3 grid grid-cols-1 md:grid-cols-4 gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input
            data-testid="orders-search"
            placeholder="Buscar pedido, cliente, SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-full border border-slate-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#002EA6]"
          />
        </div>
        <select
          data-testid="orders-filter-company"
          value={filters.company_id}
          onChange={(e) => setFilters({ ...filters, company_id: e.target.value })}
          className="border border-slate-300 rounded-sm px-2 py-2 text-sm"
        >
          <option value="">Todas empresas</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.nome_fantasia}
            </option>
          ))}
        </select>
        <select
          data-testid="orders-filter-marketplace"
          value={filters.marketplace}
          onChange={(e) => setFilters({ ...filters, marketplace: e.target.value })}
          className="border border-slate-300 rounded-sm px-2 py-2 text-sm"
        >
          <option value="">Todos marketplaces</option>
          {MARKETPLACES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          data-testid="orders-filter-status"
          value={filters.hub_status}
          onChange={(e) => setFilters({ ...filters, hub_status: e.target.value })}
          className="border border-slate-300 rounded-sm px-2 py-2 text-sm"
        >
          <option value="">Todos status</option>
          <option value="awaiting_review">Aguardando conferência</option>
          <option value="approved">Aprovado</option>
          <option value="sent_to_erp">Enviado ao ERP</option>
          <option value="nf_authorized">NF autorizada</option>
          <option value="nf_sent">NF enviada</option>
          <option value="separated">Separado</option>
          <option value="packed">Embalado</option>
          <option value="dispatched">Despachado</option>
          <option value="blocked">Bloqueado</option>
          <option value="rejected">Rejeitado</option>
        </select>
      </div>

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
            <tr>
              <th className="text-left px-3 py-2.5">Pedido</th>
              <th className="text-left px-3 py-2.5">Marketplace</th>
              <th className="text-left px-3 py-2.5">Empresa</th>
              <th className="text-left px-3 py-2.5">Cliente</th>
              <th className="text-left px-3 py-2.5">Status MK</th>
              <th className="text-left px-3 py-2.5">Status Vyntric</th>
              <th className="text-right px-3 py-2.5">Original</th>
              <th className="text-right px-3 py-2.5">Final</th>
              <th className="text-right px-3 py-2.5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="9" className="text-center py-10">
                  <Loader2 className="inline w-5 h-5 animate-spin text-slate-400" />
                </td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center py-12 text-slate-500 text-sm">
                  Nenhum pedido encontrado. Importe pedidos de demonstração no Dashboard.
                </td>
              </tr>
            )}
            {visible.map((o) => (
              <tr key={o.id} className="t-row border-b border-slate-100 hover:bg-slate-50/80" data-testid={`order-row-${o.order_number}`}>
                <td className="px-3 py-2.5">
                  <div className="font-mono text-xs font-bold">{o.order_number}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{o.tracking_code}</div>
                </td>
                <td className="px-3 py-2.5">
                  <MarketplaceTag id={o.marketplace} />
                </td>
                <td className="px-3 py-2.5 text-xs">{o.company_name}</td>
                <td className="px-3 py-2.5 text-xs">
                  <div>{o.customer_name}</div>
                  <div className="font-mono text-[10px] text-slate-500">{o.customer_doc}</div>
                </td>
                <td className="px-3 py-2.5">
                  <MarketStatusBadge status={o.marketplace_status} />
                </td>
                <td className="px-3 py-2.5">
                  <HubStatusBadge status={o.hub_status} />
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs text-slate-500">
                  {brl(o.value_original)}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-sm font-bold">
                  {brl(o.value_final)}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-end gap-1.5">
                    <a
                      data-testid={`order-guide-${o.order_number}`}
                      href={`${API_BASE}/orders/${o.id}/picking-guide.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 hover:bg-slate-200 text-slate-600 hover:text-slate-900"
                      title="Guia de separação"
                    >
                      <FileText className="w-4 h-4" />
                    </a>
                    {["nf_sent", "label_ready", "in_separation", "separated", "packed", "dispatched"].includes(
                      o.hub_status
                    ) && (
                      <a
                        data-testid={`order-label-${o.order_number}`}
                        href={`${API_BASE}/orders/${o.id}/label.pdf`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 hover:bg-slate-200 text-slate-600 hover:text-slate-900"
                        title="Etiqueta"
                      >
                        <Printer className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
